import {
  computed,
  ref,
  watch,
  type ComputedRef,
  type Ref,
} from 'vue';

import type { Card, Word } from '@/types/model';
import { randomUuid } from '@/practice/crypto';
import { convertRubyToRaw } from '@/practice/util';
import {
  SAVED_WORDS_CACHE_SCHEMA_VERSION,
  createBrowserSavedWordsCacheBroadcast,
  createBrowserSavedWordsCacheStorage,
  type SavedWordsCacheBroadcastAdapter,
  type SavedWordsCacheBroadcastMessage,
  type SavedWordsCacheRecord,
  type SavedWordsCacheStorageAdapter,
} from '@/practice/savedWordsCacheStorage';

export type SavedWordsPayload = {
  words: Word[];
  cards: Card[];
};

type SavedWordsApiResult = {
  data: Ref<SavedWordsPayload | null>;
  isFetching: Ref<boolean>;
  error: Ref<number | null>;
};

export interface SavedWordsApi {
  getWords(
    getDefinitions: boolean,
    language: string,
    result: SavedWordsApiResult,
    addedAfter?: number,
  ): Promise<void>;
}

type SavedWordsCacheEntry = {
  data: Ref<SavedWordsPayload | null>;
  isLoading: Ref<boolean>;
  isRevalidating: Ref<boolean>;
  error: Ref<number | null>;
  hydrated: boolean;
  generation: number;
  updatedAtMs: number;
  maxAddedTime: number;
  normalizedWordIds: Map<string, number[]>;
};

type SavedWordsFetchOutcome = {
  data: SavedWordsPayload | null;
  error: number | null;
};

export type SavedWordsCacheMutation = {
  rollback: () => void;
};

export type OptimisticSavedWord = {
  temporaryWordId: number;
  commit: (wordId: number) => void;
  rollback: () => void;
};

type CacheContextRef = Readonly<Ref<string | null | undefined>>;

export type SavedWordsCacheBinding = {
  result: {
    data: ComputedRef<SavedWordsPayload | null>;
    isFetching: ComputedRef<boolean>;
    error: ComputedRef<number | null>;
  };
  isRevalidating: ComputedRef<boolean>;
  ensureLoaded: () => Promise<void>;
  refreshDelta: () => Promise<void>;
  isWordSaved: (normalizedWord: string) => boolean;
  wordIdsForNormalized: (normalizedWord: string) => number[];
  patchWord: (word: Word) => SavedWordsCacheMutation | null;
  removeWord: (wordId: number) => SavedWordsCacheMutation | null;
  patchCardsEnabled: (
    cardIds: number[],
    wordIds: number[],
    enabled: boolean,
  ) => SavedWordsCacheMutation | null;
  patchCard: (card: Card) => SavedWordsCacheMutation | null;
  patchCards: (cards: Card[]) => SavedWordsCacheMutation | null;
  addOptimisticWord: (
    word: string,
    sentence: string,
    messageId: string,
  ) => OptimisticSavedWord | null;
};

const normalizeSavedWord = (word: string): string => {
  return convertRubyToRaw(word).toLowerCase().trim();
};

const cloneWord = (word: Word): Word => {
  return {
    ...word,
    characterMeanings: word.characterMeanings?.map(meaning => ({ ...meaning })),
  };
};

const clonePayload = (payload: SavedWordsPayload): SavedWordsPayload => {
  return {
    words: payload.words.map(cloneWord),
    cards: payload.cards.map(card => ({ ...card })),
  };
};

const maxAddedTime = (words: Word[]): number => {
  let maximum = 0;
  for (const word of words) {
    maximum = Math.max(maximum, word.addedTime);
  }
  return maximum;
};

const buildNormalizedWordIds = (words: Word[]): Map<string, number[]> => {
  const wordIds = new Map<string, number[]>();
  for (const word of words) {
    const normalized = normalizeSavedWord(word.word);
    const existingIds = wordIds.get(normalized);
    if (existingIds === undefined) {
      wordIds.set(normalized, [word.id]);
    } else {
      existingIds.push(word.id);
    }
  }
  return wordIds;
};

const isRecordObject = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object';
};

const isOptionalString = (value: unknown): boolean => {
  return value === undefined || value === null || typeof value === 'string';
};

const isValidCharacterMeanings = (value: unknown): boolean => {
  return value === undefined
    || value === null
    || (
      Array.isArray(value)
      && value.every(meaning => (
        isRecordObject(meaning)
        && typeof meaning.character === 'string'
        && typeof meaning.meaning === 'string'
      ))
    );
};

const isValidWord = (value: unknown): value is Word => {
  return isRecordObject(value)
    && typeof value.id === 'number'
    && typeof value.userId === 'string'
    && typeof value.word === 'string'
    && typeof value.wordWithTransliteration === 'string'
    && typeof value.sentence === 'string'
    && (value.messageId === null || typeof value.messageId === 'string')
    && typeof value.addedTime === 'number'
    && typeof value.language === 'string'
    && typeof value.shortDefinitionSourceLang === 'string'
    && typeof value.shortDefinitionTargetLang === 'string'
    && typeof value.longDefinitionSourceLang === 'string'
    && typeof value.longDefinitionTargetLang === 'string'
    && isOptionalString(value.partOfSpeech)
    && isOptionalString(value.grammaticalGender)
    && isOptionalString(value.definiteArticle)
    && isOptionalString(value.imageUrl)
    && isValidCharacterMeanings(value.characterMeanings)
    && isOptionalString(value.sharedExampleSentence)
    && isOptionalString(value.sharedExampleSentenceAudioS3Link)
    && isOptionalString(value.wordAudioS3Link)
    && isOptionalString(value.sentenceAudioS3Link);
};

const isValidCard = (value: unknown): value is Card => {
  return isRecordObject(value)
    && typeof value.id === 'number'
    && typeof value.wordId === 'number'
    && typeof value.cardType === 'string'
    && typeof value.state === 'number'
    && (value.step === null || typeof value.step === 'number')
    && (value.stability === null || typeof value.stability === 'number')
    && (value.difficulty === null || typeof value.difficulty === 'number')
    && typeof value.due === 'number'
    && (value.lastReview === null || typeof value.lastReview === 'number')
    && typeof value.enabled === 'boolean';
};

const isValidCacheRecord = (
  value: unknown,
  cacheKey: string,
  userId: string,
  language: string,
): value is SavedWordsCacheRecord => {
  if (!isRecordObject(value)) {
    return false;
  }
  return value.schemaVersion === SAVED_WORDS_CACHE_SCHEMA_VERSION
    && value.cacheKey === cacheKey
    && value.userId === userId
    && value.language === language
    && Array.isArray(value.words)
    && value.words.every(isValidWord)
    && Array.isArray(value.cards)
    && value.cards.every(isValidCard)
    && typeof value.maxAddedTime === 'number'
    && typeof value.updatedAtMs === 'number';
};

export const savedWordsCacheKey = (userId: string, language: string): string => {
  return `${encodeURIComponent(userId)}::${encodeURIComponent(language)}`;
};

export const mergeSavedWordsDelta = (
  current: SavedWordsPayload,
  delta: SavedWordsPayload,
): SavedWordsPayload => {
  const wordsById = new Map<number, Word>();
  for (const word of current.words) {
    wordsById.set(word.id, word);
  }
  for (const word of delta.words) {
    wordsById.set(word.id, word);
  }

  const cardsById = new Map<number, Card>();
  for (const card of current.cards) {
    cardsById.set(card.id, card);
  }
  for (const card of delta.cards) {
    cardsById.set(card.id, card);
  }

  return {
    words: Array.from(wordsById.values()),
    cards: Array.from(cardsById.values()),
  };
};

export class SavedWordsCacheStore {
  private readonly entries = new Map<string, SavedWordsCacheEntry>();
  private readonly hydrationPromises = new Map<string, Promise<void>>();
  private readonly deltaPromises = new Map<string, Promise<void>>();
  private readonly fullPromises = new Map<string, Promise<void>>();
  private readonly initialRevalidationPromises = new Map<string, Promise<void>>();
  private readonly initialRevalidationStarted = new Set<string>();
  private readonly fullFetchStarted = new Set<string>();
  private readonly writePromises = new Map<string, Promise<void>>();
  private readonly sourceId = randomUuid();
  private readonly unsubscribeBroadcast: (() => void) | null;
  private clearGeneration = 0;
  private lastClearedAtMs = 0;
  private nextTemporaryWordId = -1;

  constructor(
    private readonly storage: SavedWordsCacheStorageAdapter,
    private readonly broadcast: SavedWordsCacheBroadcastAdapter | null,
  ) {
    this.unsubscribeBroadcast = this.broadcast?.subscribe(message => {
      this.receiveBroadcast(message);
    }) ?? null;
  }

  private getEntry(userId: string, language: string): SavedWordsCacheEntry {
    const cacheKey = savedWordsCacheKey(userId, language);
    const existing = this.entries.get(cacheKey);
    if (existing !== undefined) {
      return existing;
    }
    const entry: SavedWordsCacheEntry = {
      data: ref<SavedWordsPayload | null>(null),
      isLoading: ref(false),
      isRevalidating: ref(false),
      error: ref<number | null>(null),
      hydrated: false,
      generation: 0,
      updatedAtMs: 0,
      maxAddedTime: 0,
      normalizedWordIds: new Map<string, number[]>(),
    };
    this.entries.set(cacheKey, entry);
    return entry;
  }

  private publishPayload(
    entry: SavedWordsCacheEntry,
    payload: SavedWordsPayload,
    bumpGeneration: boolean,
    updatedAtMs: number,
    serverMaxAddedTime: number | null,
  ): void {
    if (bumpGeneration) {
      entry.generation += 1;
    }
    entry.data.value = clonePayload(payload);
    entry.normalizedWordIds = buildNormalizedWordIds(entry.data.value.words);
    entry.updatedAtMs = updatedAtMs;
    if (serverMaxAddedTime !== null) {
      entry.maxAddedTime = serverMaxAddedTime;
    }
    entry.error.value = null;
    entry.hydrated = true;
    entry.isLoading.value = false;
  }

  private createRecord(
    cacheKey: string,
    userId: string,
    language: string,
    entry: SavedWordsCacheEntry,
  ): SavedWordsCacheRecord | null {
    if (entry.data.value === null) {
      return null;
    }
    const payload = clonePayload(entry.data.value);
    return {
      schemaVersion: SAVED_WORDS_CACHE_SCHEMA_VERSION,
      cacheKey,
      userId,
      language,
      words: payload.words,
      cards: payload.cards,
      maxAddedTime: entry.maxAddedTime,
      updatedAtMs: entry.updatedAtMs,
    };
  }

  private schedulePersist(userId: string, language: string): void {
    const cacheKey = savedWordsCacheKey(userId, language);
    const clearGeneration = this.clearGeneration;
    const previous = this.writePromises.get(cacheKey) ?? Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(async () => {
        if (clearGeneration !== this.clearGeneration) {
          return;
        }
        const entry = this.entries.get(cacheKey);
        if (entry === undefined) {
          return;
        }
        const record = this.createRecord(cacheKey, userId, language, entry);
        if (record === null) {
          return;
        }
        await this.storage.write(record);
        if (clearGeneration === this.clearGeneration) {
          this.broadcast?.publish({
            type: 'snapshot',
            sourceId: this.sourceId,
            record,
          });
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (this.writePromises.get(cacheKey) === next) {
          this.writePromises.delete(cacheKey);
        }
      });
    this.writePromises.set(cacheKey, next);
  }

  private async hydrate(userId: string, language: string): Promise<void> {
    const cacheKey = savedWordsCacheKey(userId, language);
    const existingPromise = this.hydrationPromises.get(cacheKey);
    if (existingPromise !== undefined) {
      await existingPromise;
      return;
    }

    const entry = this.getEntry(userId, language);
    if (entry.hydrated) {
      return;
    }
    if (entry.data.value === null) {
      entry.isLoading.value = true;
    }
    const generation = entry.generation;
    const promise = (async () => {
      let record: SavedWordsCacheRecord | null = null;
      try {
        record = await this.storage.read(cacheKey);
      } catch {
        record = null;
      }
      if (entry.generation !== generation) {
        return;
      }
      if (record !== null && isValidCacheRecord(record, cacheKey, userId, language)) {
        this.publishPayload(
          entry,
          { words: record.words, cards: record.cards },
          false,
          record.updatedAtMs,
          record.maxAddedTime,
        );
      } else {
        if (record !== null) {
          try {
            await this.storage.remove(cacheKey);
          } catch {
            // Network loading remains available when cache cleanup fails.
          }
        }
        entry.hydrated = true;
      }
    })().finally(() => {
      this.hydrationPromises.delete(cacheKey);
    });
    this.hydrationPromises.set(cacheKey, promise);
    await promise;
  }

  private async fetchWords(
    api: SavedWordsApi,
    language: string,
    addedAfter: number | undefined,
  ): Promise<SavedWordsFetchOutcome> {
    const result: SavedWordsApiResult = {
      data: ref<SavedWordsPayload | null>(null),
      isFetching: ref(false),
      error: ref<number | null>(null),
    };
    try {
      if (addedAfter === undefined) {
        await api.getWords(true, language, result);
      } else {
        await api.getWords(true, language, result, addedAfter);
      }
    } catch {
      return { data: null, error: 0 };
    }
    if (result.error.value !== null || result.data.value === null) {
      return {
        data: null,
        error: result.error.value ?? 0,
      };
    }
    return {
      data: result.data.value,
      error: null,
    };
  }

  private updateRevalidating(cacheKey: string, entry: SavedWordsCacheEntry): void {
    entry.isRevalidating.value = this.deltaPromises.has(cacheKey)
      || this.fullPromises.has(cacheKey);
  }

  private async requestDelta(
    userId: string,
    language: string,
    api: SavedWordsApi,
  ): Promise<void> {
    const cacheKey = savedWordsCacheKey(userId, language);
    const existingPromise = this.deltaPromises.get(cacheKey);
    if (existingPromise !== undefined) {
      await existingPromise;
      return;
    }
    const entry = this.getEntry(userId, language);
    if (entry.data.value === null || entry.maxAddedTime <= 0) {
      return;
    }

    const generation = entry.generation;
    const addedAfter = entry.maxAddedTime;
    const promise = (async () => {
      const outcome = await this.fetchWords(api, language, addedAfter);
      if (entry.generation !== generation) {
        return;
      }
      if (outcome.data === null) {
        entry.error.value = outcome.error;
        return;
      }
      const current = entry.data.value;
      if (current === null) {
        return;
      }
      this.publishPayload(
        entry,
        mergeSavedWordsDelta(current, outcome.data),
        false,
        Date.now(),
        Math.max(entry.maxAddedTime, maxAddedTime(outcome.data.words)),
      );
      this.schedulePersist(userId, language);
    })().finally(() => {
      this.deltaPromises.delete(cacheKey);
      this.updateRevalidating(cacheKey, entry);
    });
    this.deltaPromises.set(cacheKey, promise);
    this.updateRevalidating(cacheKey, entry);
    await promise;
  }

  private async requestFull(
    userId: string,
    language: string,
    api: SavedWordsApi,
  ): Promise<void> {
    const cacheKey = savedWordsCacheKey(userId, language);
    const existingPromise = this.fullPromises.get(cacheKey);
    if (existingPromise !== undefined) {
      await existingPromise;
      return;
    }
    if (this.fullFetchStarted.has(cacheKey)) {
      return;
    }
    this.fullFetchStarted.add(cacheKey);

    const entry = this.getEntry(userId, language);
    const generation = entry.generation;
    if (entry.data.value === null) {
      entry.isLoading.value = true;
    }
    const promise = (async () => {
      const outcome = await this.fetchWords(api, language, undefined);
      if (entry.generation !== generation) {
        return;
      }
      if (outcome.data === null) {
        entry.error.value = outcome.error;
        return;
      }
      this.publishPayload(
        entry,
        outcome.data,
        false,
        Date.now(),
        maxAddedTime(outcome.data.words),
      );
      this.schedulePersist(userId, language);
    })().finally(() => {
      entry.isLoading.value = false;
      this.fullPromises.delete(cacheKey);
      this.updateRevalidating(cacheKey, entry);
    });
    this.fullPromises.set(cacheKey, promise);
    this.updateRevalidating(cacheKey, entry);
    await promise;
  }

  private startInitialRevalidation(
    userId: string,
    language: string,
    api: SavedWordsApi,
  ): Promise<void> {
    const cacheKey = savedWordsCacheKey(userId, language);
    const existingPromise = this.initialRevalidationPromises.get(cacheKey);
    if (existingPromise !== undefined) {
      return existingPromise;
    }
    if (this.initialRevalidationStarted.has(cacheKey)) {
      return Promise.resolve();
    }
    this.initialRevalidationStarted.add(cacheKey);

    const entry = this.getEntry(userId, language);
    const promise = (async () => {
      if (entry.data.value !== null) {
        await this.requestDelta(userId, language, api);
      }
      await this.requestFull(userId, language, api);
    })().finally(() => {
      this.initialRevalidationPromises.delete(cacheKey);
    });
    this.initialRevalidationPromises.set(cacheKey, promise);
    return promise;
  }

  async ensureLoaded(userId: string, language: string, api: SavedWordsApi): Promise<void> {
    if (userId.length === 0 || language.length === 0) {
      return;
    }
    const clearGeneration = this.clearGeneration;
    await this.hydrate(userId, language);
    if (clearGeneration !== this.clearGeneration) {
      return;
    }
    const entry = this.getEntry(userId, language);
    const initialPromise = this.startInitialRevalidation(userId, language, api);
    if (entry.data.value === null) {
      await initialPromise;
    }
  }

  async refreshDelta(userId: string, language: string, api: SavedWordsApi): Promise<void> {
    if (userId.length === 0 || language.length === 0) {
      return;
    }
    const clearGeneration = this.clearGeneration;
    await this.hydrate(userId, language);
    if (clearGeneration !== this.clearGeneration) {
      return;
    }
    const cacheKey = savedWordsCacheKey(userId, language);
    if (!this.initialRevalidationStarted.has(cacheKey)) {
      await this.startInitialRevalidation(userId, language, api);
      return;
    }
    const initialPromise = this.initialRevalidationPromises.get(cacheKey);
    if (initialPromise !== undefined) {
      await initialPromise;
      return;
    }
    const entry = this.getEntry(userId, language);
    if (entry.data.value === null) {
      return;
    }
    await this.requestDelta(userId, language, api);
  }

  getData(userId: string, language: string): SavedWordsPayload | null {
    return this.getEntry(userId, language).data.value;
  }

  isLoading(userId: string, language: string): boolean {
    return this.getEntry(userId, language).isLoading.value;
  }

  isRevalidating(userId: string, language: string): boolean {
    return this.getEntry(userId, language).isRevalidating.value;
  }

  getError(userId: string, language: string): number | null {
    return this.getEntry(userId, language).error.value;
  }

  wordIdsForNormalized(userId: string, language: string, normalizedWord: string): number[] {
    const normalized = normalizeSavedWord(normalizedWord);
    const entry = this.getEntry(userId, language);
    if (entry.data.value === null) {
      return [];
    }
    return [...(entry.normalizedWordIds.get(normalized) ?? [])];
  }

  isWordSaved(userId: string, language: string, normalizedWord: string): boolean {
    return this.wordIdsForNormalized(userId, language, normalizedWord).length > 0;
  }

  private mutate(
    userId: string,
    language: string,
    transform: (payload: SavedWordsPayload) => SavedWordsPayload,
  ): SavedWordsCacheMutation | null {
    const entry = this.getEntry(userId, language);
    if (entry.data.value === null) {
      return null;
    }
    const before = clonePayload(entry.data.value);
    this.publishPayload(entry, transform(entry.data.value), true, Date.now(), null);
    const mutationGeneration = entry.generation;
    this.schedulePersist(userId, language);
    return {
      rollback: () => {
        if (entry.generation !== mutationGeneration) {
          return;
        }
        this.publishPayload(entry, before, true, Date.now(), null);
        this.schedulePersist(userId, language);
      },
    };
  }

  patchWord(userId: string, language: string, word: Word): SavedWordsCacheMutation | null {
    return this.mutate(userId, language, payload => {
      const existingIndex = payload.words.findIndex(existing => existing.id === word.id);
      if (existingIndex === -1) {
        return {
          words: [word, ...payload.words],
          cards: payload.cards,
        };
      }
      const words = [...payload.words];
      words[existingIndex] = word;
      return {
        words,
        cards: payload.cards,
      };
    });
  }

  removeWord(userId: string, language: string, wordId: number): SavedWordsCacheMutation | null {
    return this.mutate(userId, language, payload => ({
      words: payload.words.filter(word => word.id !== wordId),
      cards: payload.cards.filter(card => card.wordId !== wordId),
    }));
  }

  patchCardsEnabled(
    userId: string,
    language: string,
    cardIds: number[],
    wordIds: number[],
    enabled: boolean,
  ): SavedWordsCacheMutation | null {
    const cardIdSet = new Set(cardIds);
    const wordIdSet = new Set(wordIds);
    return this.mutate(userId, language, payload => ({
      words: payload.words,
      cards: payload.cards.map(card => {
        if (cardIdSet.has(card.id) || wordIdSet.has(card.wordId)) {
          return { ...card, enabled };
        }
        return card;
      }),
    }));
  }

  patchCard(userId: string, language: string, card: Card): SavedWordsCacheMutation | null {
    return this.patchCards(userId, language, [card]);
  }

  patchCards(userId: string, language: string, cards: Card[]): SavedWordsCacheMutation | null {
    const cardsById = new Map(cards.map(card => [card.id, card]));
    return this.mutate(userId, language, payload => ({
      words: payload.words,
      cards: payload.cards.map(card => cardsById.get(card.id) ?? card),
    }));
  }

  addOptimisticWord(
    userId: string,
    language: string,
    wordText: string,
    sentence: string,
    messageId: string,
  ): OptimisticSavedWord | null {
    const entry = this.getEntry(userId, language);
    if (entry.data.value === null) {
      return null;
    }
    const temporaryWordId = this.nextTemporaryWordId;
    this.nextTemporaryWordId -= 1;
    const optimisticWord: Word = {
      id: temporaryWordId,
      userId,
      word: wordText,
      wordWithTransliteration: wordText,
      sentence,
      messageId,
      addedTime: Math.floor(Date.now() / 1000),
      language,
      shortDefinitionSourceLang: '',
      shortDefinitionTargetLang: '',
      longDefinitionSourceLang: '',
      longDefinitionTargetLang: '',
    };
    this.patchWord(userId, language, optimisticWord);

    return {
      temporaryWordId,
      commit: (wordId: number) => {
        this.mutate(userId, language, payload => {
          const existingWord = payload.words.find(word => word.id === wordId);
          const words = payload.words.filter(word => word.id !== temporaryWordId);
          if (existingWord === undefined) {
            words.unshift({ ...optimisticWord, id: wordId });
          }
          return {
            words,
            cards: payload.cards,
          };
        });
      },
      rollback: () => {
        this.removeWord(userId, language, temporaryWordId);
      },
    };
  }

  private receiveBroadcast(message: SavedWordsCacheBroadcastMessage): void {
    if (message.sourceId === this.sourceId) {
      return;
    }
    if (message.type === 'clear') {
      if (message.clearedAtMs <= this.lastClearedAtMs) {
        return;
      }
      void this.clearInternal(false, message.clearedAtMs);
      return;
    }

    const record = message.record;
    if (!isValidCacheRecord(record, record.cacheKey, record.userId, record.language)) {
      return;
    }
    if (record.updatedAtMs <= this.lastClearedAtMs) {
      return;
    }
    const entry = this.getEntry(record.userId, record.language);
    if (record.updatedAtMs < entry.updatedAtMs) {
      return;
    }
    this.publishPayload(
      entry,
      { words: record.words, cards: record.cards },
      true,
      record.updatedAtMs,
      record.maxAddedTime,
    );
  }

  private async clearInternal(publish: boolean, clearedAtMs: number): Promise<void> {
    this.lastClearedAtMs = Math.max(this.lastClearedAtMs, clearedAtMs);
    this.clearGeneration += 1;
    for (const entry of this.entries.values()) {
      entry.generation += 1;
      entry.data.value = null;
      entry.isLoading.value = false;
      entry.isRevalidating.value = false;
      entry.error.value = null;
      entry.hydrated = false;
      entry.updatedAtMs = this.lastClearedAtMs;
      entry.maxAddedTime = 0;
      entry.normalizedWordIds = new Map<string, number[]>();
    }
    this.initialRevalidationStarted.clear();
    this.fullFetchStarted.clear();
    await Promise.all(Array.from(this.writePromises.values()));
    await this.storage.clear();
    if (publish) {
      this.broadcast?.publish({
        type: 'clear',
        sourceId: this.sourceId,
        clearedAtMs: this.lastClearedAtMs,
      });
    }
  }

  async clear(): Promise<void> {
    const clearedAtMs = Math.max(Date.now(), this.lastClearedAtMs + 1);
    await this.clearInternal(true, clearedAtMs);
  }

  dispose(): void {
    this.unsubscribeBroadcast?.();
    this.broadcast?.close();
  }
}

export const savedWordsCache = new SavedWordsCacheStore(
  createBrowserSavedWordsCacheStorage(),
  createBrowserSavedWordsCacheBroadcast(),
);

export const clearSavedWordsCache = async (): Promise<void> => {
  await savedWordsCache.clear();
};

const readContext = (
  userId: CacheContextRef,
  language: CacheContextRef,
): { userId: string; language: string } | null => {
  const currentUserId = userId.value ?? '';
  const currentLanguage = language.value ?? '';
  if (currentUserId.length === 0 || currentLanguage.length === 0) {
    return null;
  }
  return {
    userId: currentUserId,
    language: currentLanguage,
  };
};

export const useSavedWordsCache = (
  userId: CacheContextRef,
  language: CacheContextRef,
  api: SavedWordsApi,
): SavedWordsCacheBinding => {
  const data = computed<SavedWordsPayload | null>(() => {
    const context = readContext(userId, language);
    return context === null
      ? null
      : savedWordsCache.getData(context.userId, context.language);
  });
  const isFetching = computed<boolean>(() => {
    const context = readContext(userId, language);
    return context !== null
      && savedWordsCache.isLoading(context.userId, context.language);
  });
  const isRevalidating = computed<boolean>(() => {
    const context = readContext(userId, language);
    return context !== null
      && savedWordsCache.isRevalidating(context.userId, context.language);
  });
  const error = computed<number | null>(() => {
    const context = readContext(userId, language);
    return context === null
      ? null
      : savedWordsCache.getError(context.userId, context.language);
  });

  const ensureLoaded = async (): Promise<void> => {
    const context = readContext(userId, language);
    if (context === null) {
      return;
    }
    await savedWordsCache.ensureLoaded(context.userId, context.language, api);
  };
  const refreshDelta = async (): Promise<void> => {
    const context = readContext(userId, language);
    if (context === null) {
      return;
    }
    await savedWordsCache.refreshDelta(context.userId, context.language, api);
  };

  watch(
    () => [userId.value, language.value] as const,
    () => {
      void ensureLoaded();
    },
    { immediate: true },
  );

  return {
    result: {
      data,
      isFetching,
      error,
    },
    isRevalidating,
    ensureLoaded,
    refreshDelta,
    isWordSaved: (normalizedWord: string): boolean => {
      const context = readContext(userId, language);
      return context !== null
        && savedWordsCache.isWordSaved(
          context.userId,
          context.language,
          normalizedWord,
        );
    },
    wordIdsForNormalized: (normalizedWord: string): number[] => {
      const context = readContext(userId, language);
      return context === null
        ? []
        : savedWordsCache.wordIdsForNormalized(
            context.userId,
            context.language,
            normalizedWord,
          );
    },
    patchWord: (word: Word): SavedWordsCacheMutation | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.patchWord(context.userId, context.language, word);
    },
    removeWord: (wordId: number): SavedWordsCacheMutation | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.removeWord(context.userId, context.language, wordId);
    },
    patchCardsEnabled: (
      cardIds: number[],
      wordIds: number[],
      enabled: boolean,
    ): SavedWordsCacheMutation | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.patchCardsEnabled(
            context.userId,
            context.language,
            cardIds,
            wordIds,
            enabled,
          );
    },
    patchCard: (card: Card): SavedWordsCacheMutation | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.patchCard(context.userId, context.language, card);
    },
    patchCards: (cards: Card[]): SavedWordsCacheMutation | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.patchCards(context.userId, context.language, cards);
    },
    addOptimisticWord: (
      word: string,
      sentence: string,
      messageId: string,
    ): OptimisticSavedWord | null => {
      const context = readContext(userId, language);
      return context === null
        ? null
        : savedWordsCache.addOptimisticWord(
            context.userId,
            context.language,
            word,
            sentence,
            messageId,
          );
    },
  };
};
